/* eslint-disable unicorn/no-null */
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { groupBy } from 'es-toolkit';
// mantine
import { Container, Loader, Select, Space, Tabs } from '@mantine/core';
import { useDebouncedState } from '@mantine/hooks';
// components
import styles from './Settings.module.css';
import Attribute from '@/pages/Settings/components/Attribute';
import Header from '@/components/Header';
import Toolbar from '@/pages/Settings/components/Toolbar';
import Chat from '@/pages/Settings/components/Chat';
import Voice from '@/pages/Settings/components/Voice';
// utils
import { useCustomerAttributesQuery } from '@/services/bff/bff.queries.ts';

const DEBOUNCE_TIMEOUT = 200;

const Settings = memo(() => {
    const [activeTab, setActiveTab] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useDebouncedState('', DEBOUNCE_TIMEOUT);

    const { data, isLoading } = useCustomerAttributesQuery({
        query: searchTerm,
        include_hidden: false
    });

    const { attributes = [], groups } = data ?? {};

    useEffect(() => {
        setActiveTab(groups?.at(0) ?? null);
    }, [groups]);

    const handleSearchChange = useCallback(
        (term: string) => {
            setSearchTerm(term);
        },
        [setSearchTerm]
    );

    const items = useMemo(() => {
        return groupBy(attributes, ({ group }) => (group?.trim() ? group : ''));
    }, [attributes]);

    const renderSearchResults = () => {
        if (!searchTerm || isLoading) return null;

        return (
            <div className="flex flex-col gap-4">
                {attributes.map((attribute) => (
                    <Attribute attribute={attribute} key={attribute.id} />
                ))}
            </div>
        );
    };

    const renderTabs = () => {
        if (searchTerm || isLoading) return null;

        return (
            <Tabs keepMounted={false} value={activeTab} onChange={setActiveTab}>
                <Select
                    className="md:hidden"
                    data={[...(groups ?? []), 'Voice', 'Chat']}
                    label="Select group"
                    size="sm"
                    value={activeTab}
                    onChange={setActiveTab}
                />
                <Tabs.List className="hidden md:flex">
                    {groups?.map((group) => (
                        <Tabs.Tab key={group} value={group}>
                            {group}
                        </Tabs.Tab>
                    ))}
                    <Tabs.Tab className="hidden" value="Voice">
                        Voice
                    </Tabs.Tab>
                    <Tabs.Tab value="Chat">Chat</Tabs.Tab>
                </Tabs.List>
                {groups?.map((group) => (
                    <Tabs.Panel key={group} value={group}>
                        <div className="flex flex-col gap-4 pt-4">
                            {items[group].map((attribute) => (
                                <Attribute
                                    attribute={attribute}
                                    key={attribute.id}
                                />
                            ))}
                        </div>
                    </Tabs.Panel>
                ))}
                <Tabs.Panel key="voice" value="Voice">
                    <Voice />
                </Tabs.Panel>
                <Tabs.Panel key="chat" value="Chat">
                    <Chat />
                </Tabs.Panel>
            </Tabs>
        );
    };

    return (
        <>
            <Helmet>
                <title>NEWO - Settings</title>
            </Helmet>
            <Header title="Settings" />
            <Space h={130} />
            <Toolbar
                searchTerm={searchTerm}
                onSearchChange={handleSearchChange}
            />
            <Container className={styles.container} pb={100} size="xl">
                {isLoading ? (
                    <div className="flex justify-center items-center py-24">
                        <Loader />
                    </div>
                ) : null}
                {renderSearchResults()}
                {renderTabs()}
            </Container>
        </>
    );
});

Settings.displayName = 'Settings';

export default Settings;
